import { useState, useEffect, useCallback, useRef } from 'react';
import {
  GEO_COARSE_MAX_AGE_MS,
  GEO_COARSE_TIMEOUT_MS,
  GEO_MAX_ACCURACY_METERS,
  GEO_PERMISSION_PROOF_KEY,
  GEO_STALE_AFTER_HIDDEN_MS,
  GEO_TIMEOUT_MS,
  GEO_WATCHDOG_MS,
  PROMPT_REVERIFY_DEBOUNCE_MS,
} from '../constants';
import type { ContingencyReason, GeoStatus, LocationSource } from '../types';

/**
 * Log de diagnóstico para os pontos críticos deste hook: estados, decisões (motivo
 * de cada classificação/bloqueio/reverificação) e a localização obtida (precisão,
 * lat/lng, altitude, horários). `console.debug` é interceptado pelo
 * `DebugConsolePage` (`/debug`, atrás de `labs_settings.debug`) — nenhuma infra
 * de logging nova é necessária para ficar visível em campo.
 */
function logGeo(event: string, details?: Record<string, unknown>): void {
  console.debug('[useGeolocation]', event, details);
}

interface GeoState {
  location: { lat: number; lng: number } | null;
  status: GeoStatus;
  /** Accuracy, in meters, of `location`, when known. */
  accuracy: number | null;
  geoError: string | null;
  /** True when the punch can proceed even without a confirmed location. */
  contingency: boolean;
  /** Why the punch can proceed under contingency; `null` when `contingency` is `false`. */
  contingencyReason: ContingencyReason | null;
  /** True when the punch must stay blocked until the condition is resolved. */
  blocked: boolean;
  /** True while the degraded second attempt is in flight, so the wait can be explained. */
  retrying: boolean;
  /** Ms from the start of the loading episode to the accepted position or the verdict. */
  locationElapsedMs: number | null;
  /** Which path produced the exposed state; `null` while loading. */
  locationSource: LocationSource | null;
}

const GEO_MESSAGES: Record<Exclude<GeoStatus, 'loading' | 'success' | 'inaccurate'>, string> = {
  timeout: 'Localização não obtida: a coleta será feita sob regime de contingência.',
  unavailable: 'Localização não obtida: a coleta será feita sob regime de contingência.',
  unsupported:
    'Este navegador não oferece a API de localização. A coleta não será autorizada.',
  // Estado transitório, não veredito: assim que o navegador registrar a
  // concessão, o hook refaz a leitura sozinho e libera o registro.
  'permission-prompt':
    'Aguardando a permissão de localização do navegador. Conceda a permissão para liberar o registro.',
  'permission-denied': 'A permissão do navegador foi negada. A coleta não será autorizada.',
};

const CONTINGENCY_STATUSES: ReadonlySet<GeoStatus> = new Set([
  'timeout',
  'unavailable',
  'inaccurate',
]);
const BLOCKED_STATUSES: ReadonlySet<GeoStatus> = new Set([
  'permission-denied',
  'permission-prompt',
  'unsupported',
]);

/**
 * LIMITAÇÃO TÉCNICA DO AMBIENTE — WebKit (Safari macOS/iOS, e todo navegador no
 * iOS, que é obrigado a usar o WebKit).
 *
 * O Safari 16+ ACEITA `permissions.query({ name: 'geolocation' })` — não lança
 * erro — mas responde `'prompt'` de forma permanente, mesmo depois de o usuário
 * ter concedido ou negado a permissão. A geolocalização em si funciona: o
 * usuário é perguntado uma vez e as leituras seguintes respondem normalmente.
 * É só o `PermissionStatus.state` que não reflete a realidade.
 *
 * Bug aberto desde 2024-11: https://github.com/mdn/browser-compat-data/issues/25032
 *
 * MEDIÇÃO DE CAMPO (2026-08-31, iPhone 13, Safari 26.6.1 e Chrome iOS 152):
 * o bug NÃO se reproduz — `permissions.query` transicionou corretamente de
 * `prompt` para `granted` após a concessão, nos dois navegadores. Ou seja, no
 * WebKit atual o nível 2 abaixo funciona sozinho.
 *
 * O nível 1 continua valendo como redundância defensiva: cobre aparelhos com
 * iOS mais antigo (o bug é da era iOS 18 e não foi testado lá) e é evidência
 * mais forte que a própria Permissions API.
 *
 * Por isso a verificação tem três níveis, do mais forte para o mais fraco:
 *
 *   1. Uma leitura bem-sucedida nesta SESSÃO (persistida em `sessionStorage`,
 *      por isso sobrevive à remontagem do componente). É PROVA por observação:
 *      o navegador só devolve coordenadas com a permissão concedida. Funciona
 *      em todo motor, WebKit incluído.
 *   2. `permissions.query`. Autoritativo onde funciona (Chrome, Firefox, Edge).
 *   3. Nada disso — permissão INVERIFICÁVEL neste navegador.
 *
 * Uma permissão NEGADA continua bloqueando em qualquer nível: `PERMISSION_DENIED`
 * é tratado antes, a partir do código do erro, e nenhuma evidência o sobrepõe.
 * Uma negação também invalida a prova do nível 1, que é revogável.
 */

/**
 * Como tratar o navegador em que a permissão é INVERIFICÁVEL — sem Permissions
 * API, ou com a API rejeitando o nome `geolocation`. Não confundir com a API
 * respondendo `'prompt'`: isso é evidência POSITIVA de não-concessão e bloqueia
 * sempre, independentemente desta constante.
 *
 * `true` porque um navegador que não sabe informar o estado da permissão não é
 * evidência de que o trabalhador a negou — e tratá-lo como negativa trancava o
 * registro sem saída possível. Decisão registrada em `docs/specs/core-geolocation.md`.
 */
const CONTINGENCY_WHEN_PERMISSION_UNVERIFIABLE = true;

/** Evidência do nível 2, com `'unknown'` para o navegador que não sabe informar. */
type PermissionEvidence = PermissionState | 'unknown';

function readPermissionProof(): boolean {
  try {
    return sessionStorage.getItem(GEO_PERMISSION_PROOF_KEY) === 'true';
  } catch {
    // Modo privado/storage bloqueado: a prova apenas não sobrevive à remontagem.
    return false;
  }
}

function writePermissionProof(): void {
  try {
    sessionStorage.setItem(GEO_PERMISSION_PROOF_KEY, 'true');
  } catch {
    // Idem: degradar para prova só em memória é aceitável.
  }
}

function clearPermissionProof(): void {
  try {
    sessionStorage.removeItem(GEO_PERMISSION_PROOF_KEY);
  } catch {
    // Idem.
  }
}

/**
 * Nível 2 da verificação. Devolve `'unknown'` tanto quando a API não existe
 * quanto quando ela rejeita o nome `geolocation` — os dois casos em que o
 * navegador é incapaz de informar, e que não podem ser lidos como negativa.
 */
async function queryPermissionState(): Promise<PermissionEvidence> {
  if (!navigator.permissions?.query) return 'unknown';
  try {
    const result = await navigator.permissions.query({ name: 'geolocation' });
    return result.state;
  } catch {
    return 'unknown';
  }
}

/**
 * Classifies a failed reading: a denial is terminal, and every other failure is
 * a contingency once the permission is established — by a successful reading in
 * this session, by the Permissions API confirming it, or by the permission being
 * unverifiable in this browser.
 *
 * @param hadSuccessfulReading - Nível 1: já houve leitura bem-sucedida nesta sessão.
 */
async function resolveErrorStatus(
  err: GeolocationPositionError,
  hadSuccessfulReading: boolean,
): Promise<GeoStatus> {
  if (err.code === err.PERMISSION_DENIED) {
    logGeo('permission-decision', {
      level: 'error-code',
      evidence: 'PERMISSION_DENIED',
      decision: 'permission-denied',
    });
    return 'permission-denied';
  }
  const failure: GeoStatus = err.code === err.TIMEOUT ? 'timeout' : 'unavailable';
  if (hadSuccessfulReading) {
    logGeo('permission-decision', {
      level: 1,
      evidence: 'successful-reading-this-session',
      decision: failure,
    });
    return failure;
  }

  const state = await queryPermissionState();
  if (state === 'granted') {
    logGeo('permission-decision', { level: 2, evidence: state, decision: failure });
    return failure;
  }
  if (state === 'denied') {
    logGeo('permission-decision', { level: 2, evidence: state, decision: 'permission-denied' });
    return 'permission-denied';
  }
  // `'prompt'` é evidência positiva de que a permissão não foi concedida:
  // bloqueia, mas de forma recuperável — o observador abaixo refaz a leitura
  // assim que o navegador registrar a concessão.
  if (state === 'prompt') {
    logGeo('permission-decision', { level: 2, evidence: state, decision: 'permission-prompt' });
    return 'permission-prompt';
  }
  const decision = CONTINGENCY_WHEN_PERMISSION_UNVERIFIABLE ? failure : 'permission-prompt';
  logGeo('permission-decision', { level: 3, evidence: 'unverifiable', decision });
  return decision;
}

/**
 * Erro de TIMEOUT sintético do relógio de segurança (PLATFORM-3394): faz o veredito
 * passar por `resolveErrorStatus`, em vez de gravar `timeout` direto, para que uma
 * contingência continue exigindo a permissão verificada.
 */
function syntheticTimeoutError(): GeolocationPositionError {
  return {
    code: 3,
    message: 'loading watchdog',
    PERMISSION_DENIED: 1,
    POSITION_UNAVAILABLE: 2,
    TIMEOUT: 3,
  } as GeolocationPositionError;
}

/**
 * Requests the browser's geolocation for the punchcard flow and classifies the
 * outcome into a {@link GeoStatus}, distinguishing failures that still allow the
 * punch to proceed under contingency (timeout, unavailable sensor and insufficient
 * accuracy) from the ones that must block it (permission denied, permission
 * pending, and browsers without the geolocation API).
 *
 * A high-accuracy reading that fails is retried once in a degraded mode (coarse
 * position, recent cache accepted) before being classified, and a permission
 * granted after the fact re-triggers the reading instead of leaving the worker
 * stuck on a verdict the browser no longer agrees with.
 *
 * @param enabled - Whether geolocation should be requested (e.g. once consent is granted).
 * @returns The current geolocation state plus a `refresh` function to retry the request.
 */
export function useGeolocation(enabled: boolean): GeoState & { refresh: () => void } {
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [status, setStatus] = useState<GeoStatus>('loading');
  const [accuracy, setAccuracy] = useState<number | null>(null);
  // Sem isto a tela fica em "Obtendo localização…" durante as duas tentativas
  // somadas, e uma espera longa é indistinguível de um travamento.
  const [retrying, setRetrying] = useState(false);
  const [locationElapsedMs, setLocationElapsedMs] = useState<number | null>(null);
  const [locationSource, setLocationSource] = useState<LocationSource | null>(null);
  // Espelho síncrono de `status`: o relógio de segurança, os guardas de `onchange`
  // e o retorno ao primeiro plano decidem em callbacks que não podem ler o estado
  // do render (closures presas).
  const statusRef = useRef<GeoStatus>('loading');
  // Início do episódio de `loading` (monotônico para a duração; relógio de parede
  // para saber se o relógio de segurança venceu com o timer estrangulado em segundo plano).
  const episodeRef = useRef<{ perf: number; wall: number } | null>(null);
  const hiddenAtRef = useRef<number | null>(null);
  // Último estado de permissão observado: `change` repetido não é transição.
  const lastObservedPermissionRef = useRef<PermissionEvidence | null>(null);
  // Both the position callbacks and the permission query settle asynchronously:
  // only the latest request of a still-mounted hook may write state.
  const mountedRef = useRef(true);
  const requestIdRef = useRef(0);
  // PLATFORM-3334: o handler de onchange (abaixo) roda dentro de um efeito que
  // só se reinscreve quando `enabled`/`requestPosition`/`revokePermissionProof`
  // mudam — nunca a cada render — então uma closure sobre `location` (estado)
  // ficaria presa ao valor do momento em que o listener foi registrado. O ref
  // sempre reflete o valor atual.
  const locationRef = useRef<{ lat: number; lng: number } | null>(null);
  // Nível 1 da verificação de permissão: uma leitura bem-sucedida prova que a
  // permissão foi concedida. Só uma negação explícita a supera.
  const hadSuccessfulReadingRef = useRef(readPermissionProof());
  // PLATFORM-3335: evita reiniciar o ciclo de leitura (~19s) do zero a cada
  // disparo, caso o navegador reporte `onchange: prompt` repetidamente em
  // sequência.
  const lastPromptReverifyRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const revokePermissionProof = useCallback(() => {
    hadSuccessfulReadingRef.current = false;
    clearPermissionProof();
  }, []);

  const commitStatus = useCallback((next: GeoStatus) => {
    statusRef.current = next;
    setStatus(next);
  }, []);

  // Fecha o episódio de `loading`: duração e caminho que produziu o estado exposto.
  const markVerdict = useCallback((source: LocationSource) => {
    const episode = episodeRef.current;
    setLocationElapsedMs(episode ? Math.round(performance.now() - episode.perf) : null);
    setLocationSource(source);
  }, []);

  const settleFailure = useCallback(
    (err: GeolocationPositionError, source: LocationSource, isCurrent: () => boolean) => {
      if (err.code === err.PERMISSION_DENIED) revokePermissionProof();
      logGeo('reading-failed', {
        code: err.code,
        message: err.message,
        source,
        hadSuccessfulReadingThisSession: hadSuccessfulReadingRef.current,
        loggedAt: new Date().toISOString(),
      });
      void resolveErrorStatus(err, hadSuccessfulReadingRef.current).then((next) => {
        if (!isCurrent()) return;
        setRetrying(false);
        markVerdict(source);
        commitStatus(next);
        logGeo('status-resolved', { status: next, source, loggedAt: new Date().toISOString() });
      });
    },
    [commitStatus, markVerdict, revokePermissionProof],
  );

  // Bloqueio por negação: incrementa o id de requisição para que uma leitura em voo
  // (async) não sobrescreva este bloqueio ao responder depois.
  const blockDenied = useCallback(() => {
    requestIdRef.current += 1;
    revokePermissionProof();
    locationRef.current = null;
    if (mountedRef.current) {
      markVerdict('none');
      commitStatus('permission-denied');
      setLocation(null);
      setAccuracy(null);
    }
  }, [commitStatus, markVerdict, revokePermissionProof]);

  // `forceFresh` vem do botao de nova tentativa: sem ele, a leitura degradada
  // responde do cache e devolve exatamente a mesma posicao, fazendo o botao
  // parecer quebrado para quem tenta de novo.
  const requestPosition = useCallback((forceFresh = false) => {
    const requestId = ++requestIdRef.current;
    const isCurrent = () => mountedRef.current && requestIdRef.current === requestId;

    // Um reinício dentro do mesmo `loading` não abre episódio novo: o relógio de
    // segurança e a duração medida seguem do início original.
    if (statusRef.current !== 'loading' || episodeRef.current === null) {
      episodeRef.current = { perf: performance.now(), wall: Date.now() };
    }
    commitStatus('loading');
    setLocationElapsedMs(null);
    setLocationSource(null);
    locationRef.current = null;
    setLocation(null);
    setAccuracy(null);
    setRetrying(false);

    if (!navigator.geolocation) {
      markVerdict('none');
      commitStatus('unsupported');
      return;
    }

    // `attempt` é o mais próximo de "provider" que a Geolocation API expõe ao
    // JS — o navegador não informa o sensor real (GPS/Wi-Fi/rede), só se a
    // leitura veio da tentativa de alta precisão ou da degradada.
    const acceptPosition = (pos: GeolocationPosition, attempt: 'high-accuracy' | 'coarse') => {
      setRetrying(false);
      setAccuracy(pos.coords.accuracy);
      // Keep the position even when accuracy is insufficient: it is still sent
      // along with the punch under contingency, instead of being discarded.
      const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      locationRef.current = loc;
      setLocation(loc);
      const nextStatus = pos.coords.accuracy > GEO_MAX_ACCURACY_METERS ? 'inaccurate' : 'success';
      markVerdict(attempt);
      commitStatus(nextStatus);
      logGeo('position-accepted', {
        status: nextStatus,
        attempt,
        accuracy: pos.coords.accuracy,
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
        altitude: pos.coords.altitude,
        altitudeAccuracy: pos.coords.altitudeAccuracy,
        readingTimestamp: pos.timestamp,
        loggedAt: new Date().toISOString(),
      });
    };

    const onSuccess = (pos: GeolocationPosition) => {
      // Registrado antes da guarda de obsolescência: a permissão foi concedida
      // de fato, mesmo que esta resposta específica já não seja a mais recente.
      hadSuccessfulReadingRef.current = true;
      writePermissionProof();
      if (!isCurrent()) return;
      acceptPosition(pos, 'high-accuracy');
    };

    const classify = (err: GeolocationPositionError) => settleFailure(err, 'none', isCurrent);

    // Um fix de alta precisão que não fecha a tempo é o caso comum em galpão,
    // veículo ou área coberta — e não é evidência de nada sobre a permissão.
    // Antes de classificar como falha, tenta uma leitura grosseira, que aceita
    // posição recente em cache e costuma responder de imediato.
    const onHighAccuracyError = (err: GeolocationPositionError) => {
      if (!isCurrent()) return;
      if (err.code === err.PERMISSION_DENIED) {
        classify(err);
        return;
      }
      setRetrying(true);
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          hadSuccessfulReadingRef.current = true;
          writePermissionProof();
          if (!isCurrent()) return;
          // A tentativa degradada só substitui a falha se de fato CONFIRMAR a
          // posição. Um fix de antena com quilômetros de erro não confirma nada:
          // vira "Precisão insuficiente", que para quem acabou de conceder
          // localização precisa parece defeito do app. O resultado honesto é o
          // mesmo de antes de existir esta segunda tentativa — localização não
          // obtida, sob contingência. Assim o fallback nunca piora o desfecho.
          if (pos.coords.accuracy > GEO_MAX_ACCURACY_METERS) {
            classify(err);
            return;
          }
          acceptPosition(pos, 'coarse');
        },
        classify,
        {
          enableHighAccuracy: false,
          timeout: GEO_COARSE_TIMEOUT_MS,
          maximumAge: forceFresh ? 0 : GEO_COARSE_MAX_AGE_MS,
        },
      );
    };

    navigator.geolocation.getCurrentPosition(onSuccess, onHighAccuracyError, {
      enableHighAccuracy: true,
      timeout: GEO_TIMEOUT_MS,
      maximumAge: 0,
    });
  }, [commitStatus, markVerdict, settleFailure]);

  // Only auto-fires when consent is granted; further attempts only happen via
  // refresh() — do not react to status changes here, or every refresh() call
  // would trigger a second, concurrent request (the effect would see status go
  // back to 'loading' and call requestPosition again).
  useEffect(() => {
    if (enabled) requestPosition();
  }, [enabled, requestPosition]);

  // A classificação da falha lê a permissão UMA vez, no instante do erro — e no
  // primeiro acesso esse instante costuma cair antes de o trabalhador responder
  // ao diálogo nativo. Sem observar a transição, o veredito ficava travado e a
  // tela acusava falta de uma permissão recém-concedida.
  useEffect(() => {
    if (!enabled || !navigator.permissions?.query) return;
    let permissionStatus: PermissionStatus | null = null;
    let cancelled = false;

    // PLATFORM-3334/3335: as três ramificações abaixo tratam a transição de
    // forma atômica em vez de reagir a qualquer disparo do navegador
    // incondicionalmente.
    //
    // PLATFORM-3394: dois guardas contra a rajada de eventos, que reiniciava a
    // leitura a cada aviso e mantinha a tela em `loading` para sempre quando os
    // avisos chegavam em intervalo menor que o ciclo (~19s): (1) `change` que
    // repete o último estado observado não é transição; (2) não reinicia enquanto
    // há leitura em andamento — a chamada nativa pendente prossegue sozinha quando
    // o trabalhador responde ao diálogo. `denied` nunca é ignorado.
    const onChange = () => {
      const state = permissionStatus?.state;
      const previous = lastObservedPermissionRef.current;
      lastObservedPermissionRef.current = state ?? null;
      const ignore = (reason: string) =>
        logGeo('onchange-decision', { state, decision: 'ignore', reason });

      if (state === 'granted') {
        // Só relê se ainda não há posição confirmada. Uma leitura já aceita
        // (success/inaccurate) é evidência de nível 1 mais forte que a API de
        // permissões (SPEC) — refazer a leitura aqui descartaria uma posição
        // válida e prenderia o trabalhador em até ~19s de "Obtendo localização…"
        // de novo. Não condiciona a `status === 'permission-prompt'`: em
        // timeout/unavailable (contingência, location null) uma concessão
        // tardia deve continuar disparando a releitura, como antes.
        if (state === previous) {
          ignore('same-state');
        } else if (statusRef.current === 'loading') {
          ignore('read-in-flight');
        } else if (locationRef.current === null) {
          logGeo('onchange-decision', { state, decision: 'reverify', reason: 'no-current-location' });
          requestPosition();
        } else {
          logGeo('onchange-decision', { state, decision: 'skip', reason: 'location-already-confirmed' });
        }
      } else if (state === 'denied') {
        logGeo('onchange-decision', { state, decision: 'block' });
        blockDenied();
      } else if (state === 'prompt') {
        // PLATFORM-3335: não confia no evento como veredito — alguns
        // navegadores relatam `prompt` mesmo com a permissão concedida e
        // funcionando (WebKit, MDN bcd#25032). Dispara uma releitura real e
        // deixa a classificação já existente (nível 1 + resolveErrorStatus)
        // decidir: se a permissão de fato caiu, a leitura falha e bloqueia por
        // esse caminho já confiável; se o navegador relatou errado, a leitura
        // tem sucesso e nada muda.
        const now = Date.now();
        if (now - lastPromptReverifyRef.current <= PROMPT_REVERIFY_DEBOUNCE_MS) {
          logGeo('onchange-decision', { state, decision: 'skip-debounced' });
        } else if (state === previous) {
          ignore('same-state');
        } else if (statusRef.current === 'loading') {
          ignore('read-in-flight');
        } else {
          lastPromptReverifyRef.current = now;
          logGeo('onchange-decision', { state, decision: 'reverify' });
          requestPosition();
        }
      }
    };

    void navigator.permissions
      .query({ name: 'geolocation' })
      .then((result) => {
        if (cancelled) return;
        permissionStatus = result;
        lastObservedPermissionRef.current = result.state;
        result.addEventListener?.('change', onChange);
      })
      .catch(() => {
        // Navegador sem o nome `geolocation`: não há transição a observar.
      });

    return () => {
      cancelled = true;
      permissionStatus?.removeEventListener?.('change', onChange);
    };
  }, [enabled, requestPosition, blockDenied]);

  // PLATFORM-3394: relógio de segurança. O hook dependia só do `timeout` nativo; um
  // navegador que não chama nenhum callback (diálogo aberto, WebView sem handler de
  // permissão, provedor travado) deixava a tela em `loading` para sempre. Arma ao
  // entrar em `loading` (o efeito só reexecuta quando `status` muda, então um
  // reinício interno da leitura não zera o relógio) e dispara o veredito por
  // `resolveErrorStatus` com um TIMEOUT sintético. Não incrementa `requestId`: uma
  // posição que chegue depois ainda é aceita e substitui o veredito.
  const fireWatchdog = useCallback(() => {
    if (!mountedRef.current || statusRef.current !== 'loading') return;
    const requestId = requestIdRef.current;
    const episode = episodeRef.current;
    logGeo('watchdog-fired', {
      elapsedMs: episode ? Math.round(performance.now() - episode.perf) : null,
      watchdogMs: GEO_WATCHDOG_MS,
      loggedAt: new Date().toISOString(),
    });
    settleFailure(
      syntheticTimeoutError(),
      'watchdog',
      () =>
        mountedRef.current &&
        requestIdRef.current === requestId &&
        statusRef.current === 'loading',
    );
  }, [settleFailure]);

  useEffect(() => {
    if (!enabled || status !== 'loading') return;
    const episode = episodeRef.current;
    const remaining = Math.max(0, GEO_WATCHDOG_MS - (episode ? Date.now() - episode.wall : 0));
    logGeo('watchdog-armed', { remainingMs: remaining });
    const timer = setTimeout(fireWatchdog, remaining);
    return () => clearTimeout(timer);
  }, [enabled, status, fireWatchdog]);

  // PLATFORM-3394: o app que sai e volta provavelmente passou tempo (a posição pode
  // ter mudado) ou teve permissão/GPS alterados — e o navegador pode não ter
  // emitido `change` nem rodado o timer com a página oculta.
  useEffect(() => {
    if (!enabled) return;

    // Relido a cada uso: o estado muda durante o `await` abaixo.
    const readStatus = (): GeoStatus => statusRef.current;

    const onForeground = async (hiddenForMs: number) => {
      logGeo('foreground-return', { hiddenForMs, status: readStatus() });
      if (readStatus() === 'loading') {
        const episode = episodeRef.current;
        if (episode && Date.now() - episode.wall >= GEO_WATCHDOG_MS) fireWatchdog();
        return;
      }
      if (readStatus() === 'unsupported') return;

      const evidence = await queryPermissionState();
      if (!mountedRef.current || readStatus() === 'loading') return;
      const current = readStatus();
      // O guarda de `change` compara com o último estado observado: sem registrar o
      // que a consulta acabou de ver, um `granted` posterior à revogação silenciosa
      // seria lido como repetição e a concessão não recuperaria sozinha.
      if (evidence !== 'unknown') lastObservedPermissionRef.current = evidence;
      if (evidence === 'denied') {
        if (current !== 'permission-denied') {
          logGeo('foreground-decision', { evidence, decision: 'block' });
          blockDenied();
        }
      } else if (
        evidence === 'granted' &&
        (current === 'permission-denied' || current === 'permission-prompt')
      ) {
        logGeo('foreground-decision', { evidence, decision: 'reverify', reason: 'permission-granted' });
        requestPosition();
      } else if (current !== 'permission-denied' && hiddenForMs > GEO_STALE_AFTER_HIDDEN_MS) {
        logGeo('foreground-decision', { evidence, decision: 'reverify', reason: 'stale-position' });
        requestPosition();
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now();
        return;
      }
      const hiddenAt = hiddenAtRef.current;
      hiddenAtRef.current = null;
      void onForeground(hiddenAt === null ? 0 : Date.now() - hiddenAt);
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [enabled, fireWatchdog, blockDenied, requestPosition]);

  // O botao de nova tentativa precisa ignorar o cache, ou nao tenta nada.
  const refreshFresh = useCallback(() => requestPosition(true), [requestPosition]);

  const geoError =
    status === 'loading' || status === 'success'
      ? null
      : status === 'inaccurate'
        ? `Precisão da localização insuficiente (${Math.round(accuracy ?? 0)}m, máximo aceito ${GEO_MAX_ACCURACY_METERS}m): a coleta será feita sob regime de contingência.`
        : GEO_MESSAGES[status];

  const isContingency = CONTINGENCY_STATUSES.has(status);

  return {
    location,
    status,
    accuracy,
    geoError,
    contingency: isContingency,
    contingencyReason: isContingency ? (status as ContingencyReason) : null,
    blocked: BLOCKED_STATUSES.has(status),
    retrying,
    locationElapsedMs,
    locationSource,
    refresh: refreshFresh,
  };
}
